import React from 'react'

import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import Chip from '@mui/material/Chip'
import Button from '@mui/material/Button'
import Divider from '@mui/material/Divider'
import LaunchIcon from '@mui/icons-material/Launch'

/**
 * Generic resource list item component.
 *
 * Props:
 *   Icon                   – MUI icon component class (e.g. PlayArrowIcon)
 *   listStyle              – Style object (e.g. VideoListStyle)
 *   modal                  – Pre-rendered modal element
 *   title                  – Title string to display
 *   date                   – Date string to display
 *   dateLabel              – Label prefix for the date, e.g. "Published:" or "Updated:"
 *   content_tags           – Array of content tag strings
 *   medical_specialty_tags – Array of medical specialty tag strings
 *   is_seminal_work        – Boolean; renders a "Seminal" badge if true
 *   onOpen                 – Callback invoked when "View Resource" is clicked
 *   buttonText             – Label for the view button (default: "View Resource")
 *   showButton             – Whether to render the view button (default: true)
 *   topContainerExtra      – Optional JSX rendered in the top container (e.g. ratings)
 *   secondaryContent       – Optional JSX rendered after tags (resource-specific metadata)
 *   extraActions           – Optional extra action elements alongside modal and button
 */
const ResourceList = ({
    Icon,
    listStyle,
    modal,
    title,
    date,
    dateLabel = 'Published:',
    content_tags,
    medical_specialty_tags,
    is_seminal_work,
    onOpen,
    buttonText = 'View Resource',
    showButton = true,
    topContainerExtra,
    secondaryContent,
    extraActions,
    viewControl,
}) => {
    return (
        <>
            <Box sx={listStyle.topContainer}>
                <Box sx={listStyle.topContainerIconContainer}>
                    <Icon sx={listStyle.icon} />
                    <Box>
                        <Typography {...listStyle.title} gutterBottom>
                            &nbsp;{title}
                        </Typography>
                        {date && (
                            <Typography variant='body2' color='text.secondary'>
                                {dateLabel} {date}
                            </Typography>
                        )}
                    </Box>
                </Box>
                {topContainerExtra}
            </Box>
            <Box>
                {secondaryContent}
                {content_tags && content_tags.length > 0 && (
                    <Box sx={{ mt: 1 }}>
                        {content_tags.map(tag => <Chip sx={listStyle.chips} key={tag} label={tag} />)}
                    </Box>
                )}
                {medical_specialty_tags && medical_specialty_tags.length > 0 && (
                    <Box sx={{ mt: 1 }}>
                        {medical_specialty_tags.map(tag => <Chip sx={listStyle.chips} key={tag} label={tag} />)}
                    </Box>
                )}
                {is_seminal_work && (
                    <Box sx={{ mt: 1 }}>
                        <Chip sx={listStyle.chips} label="Seminal" />
                    </Box>
                )}
                <Box sx={listStyle.actions}>
                    {modal}
                    {viewControl
                        ? viewControl({ buttonProps: listStyle.view.props, sx: listStyle.view.style })
                        : showButton && (
                            <Button {...listStyle.view.props} sx={listStyle.view.style} onClick={onOpen}>
                                {buttonText}&nbsp;<LaunchIcon fontSize='small' />
                            </Button>
                        )}
                    {extraActions}
                </Box>
            </Box>
            <Divider />
        </>
    )
}

export default ResourceList
